import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { StorybookPage, StorybookPreview } from '../../../../../core/models/storybook.models';

type PreviewViewMode = 'book' | 'scroll';

/**
 * Full-screen storybook preview — a two-page "book" spread with prev/next
 * navigation, plus a single-column "scroll" mode. Purely presentational:
 * the parent supplies the StorybookPreview data (from GET
 * /Storybooks/PreviewStorybook/{storybookId}) and is told when to close.
 */
@Component({
  selector: 'app-storybook-preview-modal',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './storybook-preview-modal.html',
  styleUrl: './storybook-preview-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StorybookPreviewModal {
  @Input({ required: true }) data!: StorybookPreview;
  @Output() closed = new EventEmitter<void>();

  readonly viewMode = signal<PreviewViewMode>('book');

  /** Index of the left-hand page in the current two-page spread (book view). */
  readonly currentSpreadIndex = signal(0);
  /** Index of the page currently in focus in scroll view. */
  readonly currentScrollIndex = signal(0);

  readonly pages = computed<StorybookPage[]>(() =>
    [...(this.data.pages ?? [])].sort((a, b) => a.pageOrder - b.pageOrder),
  );

  readonly totalPages = computed(() => this.pages().length);

  readonly leftPage = computed<StorybookPage | null>(
    () => this.pages()[this.currentSpreadIndex()] ?? null,
  );
  readonly rightPage = computed<StorybookPage | null>(
    () => this.pages()[this.currentSpreadIndex() + 1] ?? null,
  );

  /** 1-based page currently highlighted as active in the pagination bar. */
  readonly activePageNumber = computed(() =>
    (this.viewMode() === 'book' ? this.currentSpreadIndex() : this.currentScrollIndex()) + 1,
  );

  readonly canGoPrev = computed(() =>
    this.viewMode() === 'book' ? this.currentSpreadIndex() > 0 : this.currentScrollIndex() > 0,
  );
  readonly canGoNext = computed(() =>
    this.viewMode() === 'book'
      ? this.currentSpreadIndex() + 2 < this.totalPages()
      : this.currentScrollIndex() + 1 < this.totalPages(),
  );

  readonly pageRangeLabel = computed(() => {
    const total = this.totalPages();
    if (this.viewMode() === 'book') {
      const start = Math.min(this.currentSpreadIndex() + 1, total);
      const end = Math.min(this.currentSpreadIndex() + 2, total);
      return total ? `Page ${start}-${end} of ${total}` : 'No pages yet';
    }
    return total ? `Page ${this.currentScrollIndex() + 1} of ${total}` : 'No pages yet';
  });

  /**
   * Compact page-number list for the pagination bar (shared by book and
   * scroll view) — the current page(s), an ellipsis, then the final page(s)
   * (e.g. "4 5 … 27 28"), mirroring the Figma pagination control instead of
   * a plain prev/next-only strip. Book view shows the current two-page
   * spread; scroll view shows just the single current page.
   */
  readonly pageNumbers = computed<(number | '…')[]>(() => {
    const total = this.totalPages();
    if (total === 0) return [];

    const isBook = this.viewMode() === 'book';
    const current = (isBook ? this.currentSpreadIndex() : this.currentScrollIndex()) + 1;
    const currentGroup = isBook && total > current ? [current, current + 1] : [current];
    const lastGroup = isBook && total > 1 ? [Math.max(total - 1, 1), total] : [total];

    const list: (number | '…')[] = [];
    const seen = new Set<number>();
    const pushUnique = (n: number) => {
      if (!seen.has(n)) {
        seen.add(n);
        list.push(n);
      }
    };

    currentGroup.forEach(pushUnique);
    if (lastGroup[0] > currentGroup[currentGroup.length - 1] + 1) {
      list.push('…');
    }
    lastGroup.forEach(pushUnique);
    return list;
  });

  setView(mode: PreviewViewMode): void {
    this.viewMode.set(mode);
  }

  /** Jumps to the given (1-based) page — a two-page spread in book view, a single page in scroll view. */
  goToPage(page: number): void {
    const total = this.totalPages();
    const targetIndex = Math.min(Math.max(page - 1, 0), Math.max(total - 1, 0));

    if (this.viewMode() === 'book') {
      this.currentSpreadIndex.set(targetIndex % 2 === 0 ? targetIndex : targetIndex - 1);
    } else {
      this.currentScrollIndex.set(targetIndex);
      this.scrollToCurrent();
    }
  }

  /** Splits narrative body text into a short tagline and the remaining paragraph. */
  firstSentence(text: string): string {
    const match = text.match(/^[^.!?]+[.!?]/);
    return (match?.[0] ?? text).trim();
  }

  remainingText(text: string): string {
    const tagline = this.firstSentence(text);
    return text.slice(tagline.length).trim();
  }

  goPrev(): void {
    if (this.viewMode() === 'book') {
      this.currentSpreadIndex.update((i) => Math.max(0, i - 2));
    } else {
      this.currentScrollIndex.update((i) => Math.max(0, i - 1));
      this.scrollToCurrent();
    }
  }

  goNext(): void {
    if (this.viewMode() === 'book') {
      this.currentSpreadIndex.update((i) => (i + 2 < this.totalPages() ? i + 2 : i));
    } else {
      this.currentScrollIndex.update((i) => Math.min(this.totalPages() - 1, i + 1));
      this.scrollToCurrent();
    }
  }

  private scrollToCurrent(): void {
    document
      .getElementById(`fm-preview-page-${this.currentScrollIndex()}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  close(): void {
    this.closed.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.close();
  }
}
